import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LineChart, Users } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../../components/layout/PageHeader';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { ApiError } from '../../lib/api';
import { bulkAssignManager } from '../accounts';
import { useCurrentUser } from '../auth';
import {
  aggregate,
  aggregateByAmAndBrand,
  applyDraft,
  deriveFilterOptions,
  draftDeltaByAm,
  draftMoves,
  filterRoster,
  groupMovesByTarget,
  type AccountDraft,
  type DraftMove,
} from '../../lib/accountAnalytics';
import type { AccountAnalyticsFilters, AmDirectoryEntry, AnalyticsLens } from '../../types/accountAnalytics';
import { getAccountAnalyticsRoster } from './api';
import { AnalyticsFilterBar } from './AnalyticsFilterBar';
import { AnalyticsStatCards } from './AnalyticsStatCards';
import { AmByBrandChart, BreakdownBarChart, BreakdownPieChart } from './AnalyticsCharts';
import { AnalyticsTable } from './AnalyticsTable';
import { LensSwitcher } from './LensSwitcher';
import { AmPicker } from './AmPicker';
import { DraftPanel } from './DraftPanel';
import { ConfirmReassignModal } from './ConfirmReassignModal';
import { ReassignGroupModal } from './ReassignGroupModal';
import { BusinessTimeSeriesPanel } from './BusinessTimeSeriesPanel';

const ROSTER_KEY = ['account-analytics', 'roster'] as const;

const INITIAL_FILTERS: AccountAnalyticsFilters = {
  brand: [],
  accountManagerId: [],
  group: [],
  industry: [],
  accountType: [],
  // Default to the live book only — the Status chip visibly reads "Active" so
  // it's clear accounts are scoped, not missing. Paused + cancelled stay one
  // click away via the Status filter; clearing the chip (empty = all) shows
  // everything again (filterRoster treats an empty selection as no constraint).
  status: ['active'],
  scope: 'operational',
};

type CommitError = { accountId: string; reason: string };

export function AccountAnalyticsPage() {
  const me = useCurrentUser();
  const qc = useQueryClient();
  const [lens, setLens] = useState<AnalyticsLens>('am');
  const [filters, setFilters] = useState<AccountAnalyticsFilters>(INITIAL_FILTERS);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Phase 2 draft: staged AM uid per account (null = unassign). Roster-wide.
  const [draft, setDraft] = useState<AccountDraft>(new Map());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [commitErrors, setCommitErrors] = useState<CommitError[]>([]);

  const roster = useQuery({
    queryKey: ROSTER_KEY,
    queryFn: getAccountAnalyticsRoster,
    enabled: me.role === 'admin',
  });

  const rows = roster.data?.rows ?? [];
  const amDirectory = roster.data?.amDirectory ?? [];

  // Draft overlay → filter → aggregate. Every stat card + chart recomputes live
  // against the draft (the single Phase-2 seam). Options derive from the FULL
  // roster (stable as you filter).
  const options = useMemo(() => deriveFilterOptions(rows), [rows]);
  const hasUnassigned = useMemo(() => rows.some((r) => r.accountManagerId == null), [rows]);
  const appliedRows = useMemo(() => applyDraft(rows, draft, amDirectory), [rows, draft, amDirectory]);
  const filteredRows = useMemo(() => filterRoster(appliedRows, filters), [appliedRows, filters]);
  const stats = useMemo(() => aggregate(filteredRows), [filteredRows]);
  const amByBrand = useMemo(() => aggregateByAmAndBrand(filteredRows), [filteredRows]);
  // The filtered account set — scopes the Business-lens revenue to what's visible.
  const filteredIds = useMemo(() => new Set(filteredRows.map((r) => r.id)), [filteredRows]);

  // Draft summaries — roster-wide (independent of the active filters).
  const moves = useMemo(() => draftMoves(rows, draft, amDirectory), [rows, draft, amDirectory]);
  const deltas = useMemo(() => draftDeltaByAm(rows, draft, amDirectory), [rows, draft, amDirectory]);
  const moveByAccountId = useMemo(() => new Map(moves.map((m) => [m.accountId, m])), [moves]);
  const managerCount = useMemo(() => new Set(moves.map((m) => m.toAmId)).size, [moves]);
  const draftActive = moves.length > 0;

  // Group-reassign options — the group-type accounts in the roster.
  const groups = useMemo(
    () =>
      rows
        .filter((r) => r.accountType === 'group')
        .map((r) => ({ id: r.id, name: r.name }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [rows],
  );

  const currentAmById = useMemo(
    () => new Map(rows.map((r) => [r.id, r.accountManagerId])),
    [rows],
  );

  // ---- draft mutators ----
  const stageAccounts = (ids: string[], amId: string | null) =>
    setDraft((prev) => {
      const next = new Map(prev);
      for (const id of ids) {
        // Drop no-ops so the draft only carries effective moves.
        if ((currentAmById.get(id) ?? null) === amId) next.delete(id);
        else next.set(id, amId);
      }
      return next;
    });
  const removeFromDraft = (id: string) =>
    setDraft((prev) => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  const revertDraft = () => {
    setDraft(new Map());
    setCommitErrors([]);
  };

  // ---- selection plumbing ----
  const toggleRow = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const togglePage = (ids: string[], select: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (select) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  const clearSelection = () => setSelected(new Set());

  // ---- commit ----
  const commit = useMutation({
    // Group the effective moves by TARGET AM → one audited bulk-assign call per
    // distinct target (null = Unassign). Sequential (a handful of AMs at most).
    mutationFn: async (movesToCommit: DraftMove[]) => {
      let updated = 0;
      const errors: CommitError[] = [];
      for (const { accountManagerId, accountIds } of groupMovesByTarget(movesToCommit)) {
        const res = await bulkAssignManager(accountIds, accountManagerId);
        updated += res.updated;
        errors.push(...res.errors);
      }
      return { updated, errors, committedIds: movesToCommit.map((m) => m.accountId) };
    },
    onSuccess: (res) => {
      const failed = new Set(res.errors.map((e) => e.accountId));
      // Clear the succeeded accounts from the draft; keep the failed ones.
      setDraft((prev) => {
        const next = new Map(prev);
        for (const id of res.committedIds) if (!failed.has(id)) next.delete(id);
        return next;
      });
      setCommitErrors(res.errors);
      setConfirmOpen(false);
      void qc.invalidateQueries({ queryKey: ROSTER_KEY });
      if (failed.size === 0) {
        toast.success(`Reassigned ${res.updated} account${res.updated === 1 ? '' : 's'}.`);
      } else {
        toast.error(
          `${failed.size} of ${res.committedIds.length} failed. Failed accounts kept in the draft.`,
        );
      }
    },
    onError: (e) => {
      setConfirmOpen(false);
      toast.error(e instanceof ApiError ? e.message : "Couldn't reassign accounts.");
    },
  });

  if (me.role !== 'admin') {
    return <p className="text-sm text-slate-600">Only admins can view account analytics.</p>;
  }

  return (
    <div>
      <PageHeader
        title="Account Analytics"
        description="Per-AM and whole-agency view over the kyck account roster."
        actions={<LensSwitcher lens={lens} onChange={setLens} />}
      />

      {roster.isLoading ? (
        <CenteredSpinner />
      ) : roster.error ? (
        <p className="text-sm text-red-600">Couldn't load the account roster. Try refreshing.</p>
      ) : (
        <div className="space-y-6">
          {/* Top filter bar — drives the whole dashboard. Shares one `filters`
              state with the table-top copy below (two views of one source). */}
          <AnalyticsFilterBar
            testId="filter-bar-top"
            options={options}
            amDirectory={amDirectory}
            selection={filters}
            onChange={setFilters}
            hasUnassigned={hasUnassigned}
          />

          {/* Draft-mode banner — visible whenever the draft has effective moves. */}
          {draftActive && (
            <DraftPanel
              moves={moves}
              deltas={deltas}
              managerCount={managerCount}
              errors={commitErrors}
              onRevert={revertDraft}
              onConfirm={() => setConfirmOpen(true)}
            />
          )}

          {/* The charts + stats preview the draft when active (amber chrome). */}
          <div
            className={
              draftActive ? 'space-y-6 rounded-lg ring-2 ring-amber-200 ring-offset-2' : 'space-y-6'
            }
          >
            <AnalyticsStatCards stats={stats} />

            {/* Business lens v2 — invoiced income as a HISTORIC monthly time-series
                (Xero), sliced by dimension into a coloured multi-series over a
                selectable range, per currency, scoped to the filtered accounts. */}
            {lens === 'business' && (
              <BusinessTimeSeriesPanel dimRows={appliedRows} accountIds={filteredIds} />
            )}

            {lens === 'am' && <AmByBrandChart title="Accounts per AM, by brand" data={amByBrand} />}

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {lens === 'am' ? (
                <>
                  <BreakdownBarChart title="Accounts per account manager" data={stats.byAccountManager} />
                  <BreakdownPieChart title="Account type mix" data={stats.byType} />
                </>
              ) : (
                <>
                  <BreakdownBarChart title="Accounts per brand" data={stats.byBrand} />
                  <BreakdownPieChart title="Industry mix" data={stats.byIndustry} />
                </>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {lens === 'am' ? (
                <BreakdownBarChart title="Accounts per brand" data={stats.byBrand} />
              ) : (
                <BreakdownBarChart title="Accounts per group" data={stats.byGroup} />
              )}
              {/* Phase 3 placeholder — the 12-month growth line. */}
              <HistoryPlaceholder />
            </div>
          </div>

          {/* Table controls — a linked copy of the filter bar (same `filters`
              state) + the two reassignment staging entry points, sitting with
              the table they act on. */}
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <AnalyticsFilterBar
                testId="filter-bar-table"
                options={options}
                amDirectory={amDirectory}
                selection={filters}
                onChange={setFilters}
                hasUnassigned={hasUnassigned}
              />
              <Button variant="outline" size="sm" onClick={() => setGroupModalOpen(true)}>
                <Users className="h-4 w-4" />
                Reassign a group
              </Button>
            </div>

            {/* Reassign the selected rows → a picked AM. */}
            {selected.size > 0 && (
              <ReassignSelectedBar
                count={selected.size}
                amDirectory={amDirectory}
                onApply={(amId) => {
                  stageAccounts([...selected], amId);
                  clearSelection();
                }}
                onClear={clearSelection}
              />
            )}

            <AnalyticsTable
              rows={filteredRows}
              selected={selected}
              onToggleRow={toggleRow}
              onTogglePage={togglePage}
              defaultSort={lens === 'am' ? 'accountManagerName' : 'brandName'}
              moveByAccountId={moveByAccountId}
              onRemoveFromDraft={removeFromDraft}
            />
          </div>
        </div>
      )}

      <ConfirmReassignModal
        open={confirmOpen}
        moves={moves}
        deltas={deltas}
        managerCount={managerCount}
        committing={commit.isPending}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => commit.mutate(moves)}
      />

      <ReassignGroupModal
        open={groupModalOpen}
        groups={groups}
        rows={rows}
        amDirectory={amDirectory}
        onStage={stageAccounts}
        onClose={() => setGroupModalOpen(false)}
      />
    </div>
  );
}

// The selected-rows staging bar: pick a target AM and stage every selected row.
function ReassignSelectedBar({
  count,
  amDirectory,
  onApply,
  onClear,
}: {
  count: number;
  amDirectory: AmDirectoryEntry[];
  onApply: (amId: string | null) => void;
  onClear: () => void;
}) {
  const [amId, setAmId] = useState<string | null | undefined>(undefined);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-kyck-200 bg-kyck-50 px-4 py-2.5 text-sm">
      <span className="font-medium text-kyck-800">
        Reassign {count} selected account{count === 1 ? '' : 's'} →
      </span>
      <div className="flex items-center gap-2">
        <AmPicker amDirectory={amDirectory} value={amId} onChange={setAmId} />
        <Button
          size="sm"
          disabled={amId === undefined}
          onClick={() => {
            if (amId !== undefined) {
              onApply(amId);
              setAmId(undefined);
            }
          }}
        >
          Stage
        </Button>
        <Button variant="ghost" size="sm" onClick={onClear}>
          Clear
        </Button>
      </div>
    </div>
  );
}

// Phase 3 placeholder — labelled slot so the layout is ready for the history line.
function HistoryPlaceholder() {
  return (
    <div className="flex h-full min-h-56 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-200 bg-slate-50/50 p-4 text-center">
      <LineChart className="h-6 w-6 text-slate-300" />
      <p className="text-sm font-medium text-slate-400">12-month history</p>
      <p className="text-xs text-slate-400">Coming soon — growth over time per AM / agency.</p>
    </div>
  );
}
